import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _mainReactionsSummaryQuery from "./__generated__/mainReactionsSummaryQuery.graphql";
import _mainAddReactionMutation from "./__generated__/mainAddReactionMutation.graphql";
import _mainDeleteReactionMutation from "./__generated__/mainDeleteReactionMutation.graphql";
import React, { Suspense, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import ErrorIcon from '@atlaskit/icon/utility/error';
import { Inline, Text } from '@atlaskit/primitives';
import { Reactions as AkReactions } from '@atlaskit/reactions';
import { N90 } from '@atlaskit/theme/colors';
import { messages } from '../../../../index';
import { triggerReactionCreatedInContext, triggerReactionDeletedInContext, useCreateFireAnalyticsFromTrigger } from '../../../utils/analytics';
import { useExperienceTracker } from '../../../utils/experience-tracking/adapter/context';
import { Experiences } from '../../../utils/experience-tracking/types';
import { ReactionsPlaceholder } from './reactions-placeholder';
var ReactionStatus = /*#__PURE__*/function (ReactionStatus) {
  ReactionStatus["ready"] = "READY";
  ReactionStatus["loading"] = "LOADING";
  ReactionStatus["error"] = "ERROR";
  ReactionStatus["notLoaded"] = "NOT_LOADED";
  ReactionStatus["disabled"] = "DISABLED";
  return ReactionStatus;
}(ReactionStatus || {});
var noop = function noop() {};
export var ReactionsWrapperComponent = function ReactionsWrapperComponent(_ref) {
  var _reactions$reactionsS;
  var cloudId = _ref.cloudId,
    contentId = _ref.contentId,
    containerId = _ref.containerId,
    containerType = _ref.containerType,
    isInlineComment = _ref.isInlineComment,
    emojiProvider = _ref.emojiProvider,
    handleMarkAsRead = _ref.handleMarkAsRead,
    experienceTrackingAttributes = _ref.experienceTrackingAttributes;
  var experienceTracker = useExperienceTracker();
  var _useMutation = useMutation(_mainDeleteReactionMutation),
    _useMutation2 = _slicedToArray(_useMutation, 1),
    commitDeleteReaction = _useMutation2[0];
  var _useMutation3 = useMutation(_mainAddReactionMutation),
    _useMutation4 = _slicedToArray(_useMutation3, 1),
    commitAddReaction = _useMutation4[0];
  var fireReactionCreatedInContext = useCreateFireAnalyticsFromTrigger(triggerReactionCreatedInContext);
  var fireReactionDeletedInContext = useCreateFireAnalyticsFromTrigger(triggerReactionDeletedInContext);
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    refetchToggle = _useState2[0],
    setRefetchToggle = _useState2[1];
  var contentType = 'COMMENT';
  experienceTracker.start({
    name: Experiences.FETCH_REACTION_SUMMARY_IN_CONTEXT,
    id: 'fetchReactionSummary'
  });
  var reactions = useLazyLoadQuery(_mainReactionsSummaryQuery, {
    cloudId: cloudId,
    contentId: contentId,
    contentType: contentType,
    containerId: containerId,
    containerType: containerType
  }, {
    fetchPolicy: 'store-and-network',
    fetchKey: refetchToggle
  });
  var reactionsSummaryError = false;
  if (reactions.reactionsSummary !== null) {
    experienceTracker.succeed({
      name: Experiences.FETCH_REACTION_SUMMARY_IN_CONTEXT
    });
  } else {
    reactionsSummaryError = true;
    experienceTracker.fail({
      name: Experiences.FETCH_REACTION_SUMMARY_IN_CONTEXT,
      error: {
        name: 'GraphQL Error',
        message: 'Failed to fetch reaction summary'
      }
    });
  }
  var reactionsList = (_reactions$reactionsS = reactions.reactionsSummary) === null || _reactions$reactionsS === void 0 ? void 0 : _reactions$reactionsS.reactionsSummaryForEmoji.map(function (emojiSummary) {
    var _reactions$reactionsS2, _reactions$reactionsS3, _reactions$reactionsS4, _reactions$reactionsS5;
    if (!emojiSummary) {
      return null;
    }
    return {
      ari: (_reactions$reactionsS2 = (_reactions$reactionsS3 = reactions.reactionsSummary) === null || _reactions$reactionsS3 === void 0 ? void 0 : _reactions$reactionsS3.ari) !== null && _reactions$reactionsS2 !== void 0 ? _reactions$reactionsS2 : '',
      containerAri: (_reactions$reactionsS4 = (_reactions$reactionsS5 = reactions.reactionsSummary) === null || _reactions$reactionsS5 === void 0 ? void 0 : _reactions$reactionsS5.containerAri) !== null && _reactions$reactionsS4 !== void 0 ? _reactions$reactionsS4 : '',
      emojiId: emojiSummary.emojiId,
      count: emojiSummary.count,
      reacted: emojiSummary.reacted
    };
  }).filter(Boolean);
  var handleReaction = useCallback(function (emojiId, isAdd) {
    var experienceName = isAdd ? Experiences.ADD_REACTION_IN_CONTEXT : Experiences.DELETE_REACTION_IN_CONTEXT;
    var commitMutation = isAdd ? commitAddReaction : commitDeleteReaction;
    var fireAnalytics = isAdd ? fireReactionCreatedInContext : fireReactionDeletedInContext;
    experienceTracker.start({
      name: experienceName,
      id: isAdd ? 'addReaction' : 'deleteReaction',
      attributes: {
        emojiId: emojiId
      }
    });
    commitMutation({
      variables: {
        cloudId: cloudId,
        containerId: containerId,
        containerType: containerType.toUpperCase(),
        contentId: contentId,
        contentType: contentType,
        emojiId: emojiId
      },
      onCompleted: function onCompleted(response, errors) {
        if (errors) {
          experienceTracker.fail({
            name: experienceName,
            error: _objectSpread({
              name: 'GraphQL Error'
            }, errors[0])
          });
          return;
        } else {
          setRefetchToggle(function (prev) {
            return prev + 1;
          }); // Trigger refetch by updating the fetchKey
          handleMarkAsRead();
          fireAnalytics(experienceTrackingAttributes.notification, experienceTrackingAttributes.listIndex, contentId, isInlineComment ? 'INLINE_COMMENT' : 'FOOTER_COMMENT', containerId, containerType, emojiId, experienceTrackingAttributes.userId);
          experienceTracker.succeed({
            name: experienceName,
            attributes: {
              emojiId: emojiId
            }
          });
        }
      },
      onError: function onError(err) {
        experienceTracker.fail({
          name: experienceName,
          error: _objectSpread(_objectSpread({}, err), {}, {
            name: 'GraphQL Error'
          })
        });
        return;
      }
    });
  }, [commitAddReaction, commitDeleteReaction, contentId, contentType, containerId, containerType, handleMarkAsRead, cloudId, fireReactionCreatedInContext, fireReactionDeletedInContext, experienceTrackingAttributes.notification, experienceTrackingAttributes.listIndex, experienceTrackingAttributes.userId, isInlineComment, experienceTracker]);
  var onReactionClick = useCallback(function (emojiId) {
    var existingReaction = reactionsList.find(function (reaction) {
      return reaction.emojiId === emojiId;
    });
    handleReaction(emojiId, !(existingReaction !== null && existingReaction !== void 0 && existingReaction.reacted));
  }, [reactionsList, handleReaction]);
  var onSelection = useCallback(function (emojiId) {
    var existingReaction = reactionsList.find(function (reaction) {
      return reaction.emojiId === emojiId;
    });
    if (!(existingReaction !== null && existingReaction !== void 0 && existingReaction.reacted)) {
      handleReaction(emojiId, true);
    }
  }, [reactionsList, handleReaction]);
  return /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(ReactionsPlaceholder, null)
  }, reactionsSummaryError ? /*#__PURE__*/React.createElement(Inline, {
    space: "space.100",
    alignBlock: "center"
  }, /*#__PURE__*/React.createElement(ErrorIcon, {
    color: "var(--ds-icon-accent-gray, ".concat(N90, ")"),
    label: "Icon showing an error with rendering reactions"
  }), /*#__PURE__*/React.createElement(Text, {
    color: 'color.text.subtlest'
  }, /*#__PURE__*/React.createElement(FormattedMessage, messages.viewReactionsError))) : /*#__PURE__*/React.createElement(AkReactions, {
    reactions: reactionsList,
    loadReaction: noop,
    onSelection: onSelection,
    onReactionClick: onReactionClick,
    status: ReactionStatus.ready,
    emojiProvider: emojiProvider,
    summaryViewEnabled: true,
    allowAllEmojis: true
  }));
};